/**
 * Subscription feature-gate logic.
 * All functions are pure — no React imports.
 */

export type Edition = 'Free' | 'Standard' | 'Premium' | 'Enterprise';

const EDITION_ORDER: Edition[] = ['Free', 'Standard', 'Premium', 'Enterprise'];

/**
 * Compute the edition that should actually be enforced, accounting for
 * expired/cancelled states — mirrors backend logic. A trial grants access
 * to the edition being trialed (currentPlan.edition), not an automatic upgrade.
 */
export function getEffectiveEdition(
  edition: Edition,
  status: string,
  isTrial: boolean
): Edition {
  if (status === 'expired' || status === 'cancelled' || status === 'inactive') return 'Free';
  return edition;
}

export function meetsEdition(effective: Edition, min: Edition): boolean {
  return EDITION_ORDER.indexOf(effective) >= EDITION_ORDER.indexOf(min);
}

// ── Transaction type access ───────────────────────────────────────────────────

/** Minimum edition required per TxnType string value. */
const TXN_MIN_EDITION: Record<string, Edition> = {
  // Free tier
  Invoice: 'Free',
  Bills: 'Free',
  Expense: 'Free',
  // Opening types are always available
  OpeningReceivable: 'Free',
  OpeningPayables: 'Free',
  // Standard tier
  PurchaseBills: 'Standard',
  Estimate: 'Standard',
  SalesOrder: 'Standard',
  PurchaseOrder: 'Standard',
  SalesReturn: 'Standard',
  PurchaseReturn: 'Standard',
  StockAdjustment: 'Standard',
  CreditNote: 'Standard',
  DebitNote: 'Standard',
  ProformaInvoice: 'Standard',
  // Premium tier
  Delivery: 'Premium',
  ProductionOrder: 'Premium',
};

/** Minimum edition required per named module. */
const MODULE_MIN_EDITION: Record<string, Edition> = {
  Inventory: 'Standard',
  Banking: 'Standard',
  BankFeeds: 'Standard',
  AdvancedReports: 'Standard',
  BackupRestore: 'Standard',
  MultiBranch: 'Premium',
  AIAssistant: 'Premium',
  Manufacturing: 'Premium',
  Projects: 'Premium',
  Budgets: 'Premium',
  Plaid: 'Premium',
};

export function canUseTxnType(txnType: string, effectiveEdition: Edition): boolean {
  const min = TXN_MIN_EDITION[txnType];
  if (!min) return true; // unknown type → allow
  return meetsEdition(effectiveEdition, min);
}

export function canUseModule(module: string, effectiveEdition: Edition): boolean {
  const min = MODULE_MIN_EDITION[module];
  if (!min) return true;
  return meetsEdition(effectiveEdition, min);
}

export function minEditionForTxnType(txnType: string): Edition | null {
  return (TXN_MIN_EDITION[txnType] as Edition) ?? null;
}

export function minEditionForModule(module: string): Edition | null {
  return (MODULE_MIN_EDITION[module] as Edition) ?? null;
}

export function isUnlimited(limit: number): boolean {
  return limit === -1;
}
